import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { keyframes } from "@mui/system";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  startDeviceEnroll,
  deleteDeviceBiometric,
  readCardFromMachine,
  getDeviceCommandLogs,
  getDeviceBiometrics,
  cancelDeviceCommands,
  saveDeviceCard,
} from "../../pages/Apihelper/ApiHelperUser";

const POLL_MS = 3000;

const FINGERS = [
  { fid: 0, label: "Left little finger" },
  { fid: 1, label: "Left ring finger" },
  { fid: 2, label: "Left middle finger" },
  { fid: 3, label: "Left index finger" },
  { fid: 4, label: "Left thumb" },
  { fid: 5, label: "Right thumb" },
  { fid: 6, label: "Right index finger" },
  { fid: 7, label: "Right middle finger" },
  { fid: 8, label: "Right ring finger" },
  { fid: 9, label: "Right little finger" },
];

const STATUS_LABEL = {
  queued: "Waiting for machine",
  dispatched: "Sent to machine",
  success: "Done",
  failed: "Failed",
};
const STATUS_COLOR = {
  queued: "default",
  dispatched: "info",
  success: "success",
  failed: "error",
};

const ringPulse = keyframes`
  0%   { transform: scale(0.9); opacity: 0.7; }
  100% { transform: scale(1.6); opacity: 0; }
`;
const scanSweep = keyframes`
  0%   { top: 8%; }
  50%  { top: 82%; }
  100% { top: 8%; }
`;
const popIn = keyframes`
  0%   { transform: scale(0.4); opacity: 0; }
  70%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); }
`;
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-6px); }
  75% { transform: translateX(6px); }
`;

/** Fingerprint with a scanning line while we wait for the scan; tick or cross when done. */
function FingerprintAnimation({ state }) {
  const size = 88;
  const done = state === "success";
  const failed = state === "failed";
  const color = done ? "success.main" : failed ? "error.main" : "primary.main";
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
      <Box sx={{ position: "relative", width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {!done && !failed && [0, 1].map((i) => (
          <Box
            key={i}
            sx={{
              position: "absolute", inset: 0, borderRadius: "50%",
              border: "2px solid", borderColor: "primary.main",
              animation: `${ringPulse} 1.8s ease-out ${i * 0.9}s infinite`,
            }}
          />
        ))}
        {done ? (
          <CheckCircleIcon sx={{ fontSize: size, color, animation: `${popIn} 0.45s ease-out` }} />
        ) : failed ? (
          <ErrorIcon sx={{ fontSize: size, color, animation: `${shake} 0.4s ease-in-out 2` }} />
        ) : (
          <Box sx={{ position: "relative", width: size, height: size, overflow: "hidden" }}>
            <FingerprintIcon sx={{ fontSize: size, color }} />
            <Box
              sx={{
                position: "absolute", left: "6%", right: "6%", height: 3, borderRadius: 2,
                bgcolor: "primary.main", boxShadow: "0 0 10px 2px rgba(25,118,210,0.7)",
                animation: `${scanSweep} 1.6s ease-in-out infinite`,
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

const cardSlide = keyframes`
  0%   { transform: translateX(-34px) rotate(-8deg); opacity: 0.2; }
  60%  { transform: translateX(6px) rotate(0deg); opacity: 1; }
  100% { transform: translateX(6px) rotate(0deg); opacity: 1; }
`;

/** A card sliding onto a reader while we wait for the tap; tick or cross when done. */
function CardAnimation({ state }) {
  const size = 88;
  const done = state === "success";
  const failed = state === "failed";
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
      <Box sx={{ position: "relative", width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {!done && !failed && [0, 1].map((i) => (
          <Box
            key={i}
            sx={{
              position: "absolute", inset: 0, borderRadius: "50%",
              border: "2px solid", borderColor: "primary.main",
              animation: `${ringPulse} 1.8s ease-out ${i * 0.9}s infinite`,
            }}
          />
        ))}
        {done ? (
          <CheckCircleIcon sx={{ fontSize: size, color: "success.main", animation: `${popIn} 0.45s ease-out` }} />
        ) : failed ? (
          <ErrorIcon sx={{ fontSize: size, color: "error.main", animation: `${shake} 0.4s ease-in-out 2` }} />
        ) : (
          <CreditCardIcon sx={{ fontSize: size * 0.8, color: "primary.main", animation: `${cardSlide} 1.6s ease-in-out infinite` }} />
        )}
      </Box>
    </Box>
  );
}

function StepRow({ label, log }) {
  const status = log?.status || "queued";
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2">{label}</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {(status === "queued" || status === "dispatched") && <CircularProgress size={14} />}
          <Chip size="small" label={STATUS_LABEL[status] || status} color={STATUS_COLOR[status] || "default"} />
        </Stack>
      </Stack>
      {status === "failed" && log?.error_message && (
        <Typography variant="caption" color="error">{log.error_message}</Typography>
      )}
    </Stack>
  );
}

/**
 * Manage a person's fingerprints on the machine: enrol (any finger),
 * delete one finger / all fingers, and put the machine in card-enrol mode. Follows every command's
 * status from the device command logs.
 *
 * Props: open, onClose, token, personType ("employee"|"nonregister"|"member"|"user"),
 *        personId, personName, autoStart (enrol the right index finger on open,
 *        used right after adding someone; default false)
 */
export default function DeviceEnrollDialog({ open, onClose, token, personType, personId, personName, autoStart = false }) {
  const [fid, setFid] = useState(6);
  const [steps, setSteps] = useState([]); // [{ key, label, cmdId }]
  const [logs, setLogs] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [onMachine, setOnMachine] = useState({ fingers: [], cardEnrol: null });
  const [card, setCard] = useState("");
  const [cardGaveUp, setCardGaveUp] = useState(false);  // the machine read the card but sent no number
  const startedFor = useRef(null);

  const run = useCallback(async (request, buildSteps) => {
    setBusy(true);
    setError("");
    setLogs({});
    setSteps([]);
    try {
      const res = await request();
      if (String(res.code) === "200") {
        setSteps(buildSteps(res.data));
      } else {
        setError(res.message || "Could not send to the machine");
      }
    } catch (e) {
      setError("Could not reach the server");
    } finally {
      setBusy(false);
    }
  }, []);

  const loadOnMachine = useCallback(async () => {
    if (!personId) return;
    try {
      const res = await getDeviceBiometrics(token, { person_type: personType, person_id: personId });
      if (String(res.code) === "200") {
        setOnMachine({ fingers: (res.data.fingers || []).map(Number), cardEnrol: res.data.card_enrol || null });
        setCard(res.data.card ? String(res.data.card) : "");
      }
    } catch (e) { /* the list is a convenience; ignore */ }
  }, [token, personType, personId]);

  useEffect(() => {
    if (open) loadOnMachine();
  }, [open, loadOnMachine]);

  const cancelPending = async () => {
    try {
      await cancelDeviceCommands(token, steps.map((s) => s.cmdId));
    } finally {
      loadOnMachine();
    }
  };

  const fingerName = (f) => FINGERS.find((x) => x.fid === Number(f))?.label || `finger ${f}`;

  const enrolFinger = (f = fid) =>
    run(
      () => startDeviceEnroll(token, { person_type: personType, person_id: personId, mode: "finger", fid: f }),
      (d) => [
        { key: "user", label: "Add user to machine", cmdId: d.user_cmd_id },
        { key: "enroll", label: `Enrol ${fingerName(f).toLowerCase()} — scan on the machine`, cmdId: d.enroll_cmd_id },
      ]
    );

  // The card is enrolled on the machine's own menu; this reads it back and saves the number to this person.
  const readCard = () => {
    setCardGaveUp(false);
    return run(
      () => readCardFromMachine(token, { person_type: personType, person_id: personId }),
      (d) => [{ key: "cardread", label: "Read the card from the machine", cmdId: d.query_cmd_id }]
    );
  };

  const saveCard = () =>
    run(
      () => saveDeviceCard(token, { person_type: personType, person_id: personId, card_number: card }),
      (d) => [{ key: "card", label: card ? `Save card ${card} to machine` : "Remove card from machine", cmdId: d.cmd_id }]
    );

  const remove = (target, label) => {
    if (!window.confirm(`${label}? This removes it from the machine.`)) return;
    run(
      () => deleteDeviceBiometric(token, { person_type: personType, person_id: personId, target, fid }),
      (d) => [{ key: "delete", label, cmdId: d.cmd_id }]
    );
  };

  // Right after adding someone, start fingerprint enrolment once.
  useEffect(() => {
    if (open && autoStart && personId && startedFor.current !== personId) {
      startedFor.current = personId;
      enrolFinger(6);
    }
  }, [open, autoStart, personId]);

  // Follow the queued commands until they have all finished.
  useEffect(() => {
    if (!open || steps.length === 0) return undefined;
    let stopped = false;
    const ids = steps.map((s) => s.cmdId);
    const tick = async () => {
      try {
        const res = await getDeviceCommandLogs(token, { cmd_ids: ids, limit: 10 });
        if (stopped || !Array.isArray(res.data)) return;
        const byId = {};
        res.data.forEach((l) => { byId[String(l.cmd_id)] = l; });
        setLogs(byId);
      } catch (e) { /* keep polling */ }
    };
    tick();
    const timer = setInterval(tick, POLL_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [open, steps, token]);

  const last = steps.length ? logs[String(steps[steps.length - 1].cmdId)] : null;
  const waiting = steps.length > 0 && !(last && (last.status === "success" || last.status === "failed"));

  // Once the last command has finished, re-read what is on the machine.
  const lastStatus = last?.status;
  useEffect(() => {
    if (lastStatus === "success" || lastStatus === "failed") loadOnMachine();
  }, [lastStatus, loadOnMachine]);

  // After the machine reports the card was read, wait up to ~30 s for its number to reach the server.
  const cardStep = steps.find((st) => st.key === "cardread");
  const cardStatus = cardStep ? logs[String(cardStep.cmdId)]?.status : null;
  const cardResultIn = Boolean(onMachine.cardEnrol);
  useEffect(() => {
    if (!open || cardStatus !== "success" || cardResultIn) return undefined;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      loadOnMachine();
      if (tries >= 10) {
        clearInterval(timer);
        setCardGaveUp(true);
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [open, cardStatus, cardResultIn, loadOnMachine]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Machine biometrics{personName ? ` — ${personName}` : ""}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            size="small"
            label="Finger"
            value={fid}
            onChange={(e) => setFid(Number(e.target.value))}
          >
            {FINGERS.map((f) => (
              <MenuItem key={f.fid} value={f.fid}>
                {f.label}{onMachine.fingers.includes(f.fid) ? "  ✓ on machine" : ""}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {onMachine.fingers.length === 0 && (
              <Typography variant="caption" color="text.secondary">No fingerprint enrolled on the machine yet.</Typography>
            )}
            {onMachine.fingers.map((f) => (
              <Chip key={f} size="small" color="success" variant="outlined" label={fingerName(f)} />
            ))}
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" size="small" disabled={busy || waiting} onClick={() => enrolFinger()}>
              Enrol this finger
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button color="error" variant="outlined" size="small" disabled={busy || waiting}
              onClick={() => remove("finger", `Delete ${fingerName(fid).toLowerCase()}`)}>
              Delete this finger
            </Button>
            <Button color="error" variant="outlined" size="small" disabled={busy || waiting}
              onClick={() => remove("all_fingers", "Delete all fingerprints")}>
              Delete all fingers
            </Button>
          </Stack>

          <Divider />
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              label="Card number"
              value={card}
              onChange={(e) => setCard(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
              sx={{ flex: 1 }}
            />
            <Button variant="outlined" size="small" disabled={busy || waiting} onClick={saveCard}>
              Save card
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" size="small" startIcon={<CreditCardIcon />} disabled={busy || waiting} onClick={readCard}>
              Read card from machine
            </Button>
          </Stack>
          <Stack>
            <Typography variant="caption" color="text.secondary">
              1) On the machine: Menu → Users → this person → Card → tap the card → Save.
              2) Press the button. The number is read from the machine and saved here.
            </Typography>
          </Stack>

          {error && <Typography color="error" variant="body2">{error}</Typography>}
          {busy && <Typography variant="body2">Sending to machine…</Typography>}

          {steps.length > 0 && (
            <>
              <Divider />
              {steps.some((st) => st.key === "enroll") && (() => {
                const enrolLog = logs[String(steps.find((st) => st.key === "enroll").cmdId)];
                const state = enrolLog?.status === "success" ? "success" : enrolLog?.status === "failed" ? "failed" : "scanning";
                return (
                  <>
                    <FingerprintAnimation state={state} />
                    {state === "scanning" && (
                      <Typography variant="body2" align="center" fontWeight={600}>
                        {enrolLog?.status === "dispatched"
                          ? "Place the finger on the machine now"
                          : "Waiting for the machine…"}
                      </Typography>
                    )}
                  </>
                );
              })()}
              {steps.some((st) => st.key === "cardread") && (() => {
                const cardLog = logs[String(steps.find((st) => st.key === "cardread").cmdId)];
                const res = onMachine.cardEnrol;
                const state = res?.status === "duplicate" || cardLog?.status === "failed" ? "failed"
                  : res?.status === "saved" ? "success" : "scanning";
                return (
                  <>
                    <CardAnimation state={state} />
                    {state === "scanning" && (
                      <Typography variant="body2" align="center" fontWeight={600}>
                        {cardLog?.status === "success"
                          ? "Reading the card number…"
                          : "Asking the machine for the card…"}
                      </Typography>
                    )}
                    {res?.status === "saved" && (
                      <Typography variant="body2" align="center" color="success.main" fontWeight={600}>
                        Card {res.card} saved{personName ? ` for ${personName}` : ""}.
                      </Typography>
                    )}
                    {res?.status === "duplicate" && (
                      <Typography variant="body2" align="center" color="error" fontWeight={600}>
                        Card {res.card} already belongs to {res.holder}. Use a different card.
                      </Typography>
                    )}
                    {cardLog?.status === "success" && !res && cardGaveUp && (
                      <Typography variant="body2" align="center" color="warning.main">
                        The machine has no card for this person yet. Enrol the card on the machine first (Menu → Users → this person → Card), then press Read card again.
                      </Typography>
                    )}
                  </>
                );
              })()}
              {steps.map((s) => (
                <StepRow key={s.key} label={s.label} log={logs[String(s.cmdId)]} />
              ))}
              {waiting && (
                <Typography variant="body2" color="text.secondary">
                  The machine picks this up within a few seconds. Only one enrolment runs at a time.
                </Typography>
              )}
              {waiting && (
                <Button color="warning" variant="outlined" size="small" onClick={cancelPending}>
                  Cancel
                </Button>
              )}
              {last?.status === "success" && (
                <Typography variant="body2" color="success.main">Finished.</Typography>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
